import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Generate random math problem
  const generateMathProblem = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return { num1, num2, answer: num1 + num2 };
  };

  const [mathProblem, setMathProblem] = useState(generateMathProblem());
  const [mathAnswer, setMathAnswer] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    hasDog: 'no',
    howHeard: '',
    memberName: '',
    otherExplanation: ''
  });

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    
    if (value.length >= 3) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }
    if (value.length >= 10) {
      value = `${value.slice(0, 9)} ${value.slice(9)}`;
    }
    if (value.length >= 13) {
      value = `${value.slice(0, 12)} ${value.slice(12)}`;
    }

    setFormData({ ...formData, phone: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate math answer
    if (parseInt(mathAnswer) !== mathProblem.answer) {
      toast.error('Matematik işleminin sonucu yanlış. Lütfen tekrar deneyin.');
      setMathProblem(generateMathProblem());
      setMathAnswer('');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: `temp${Math.random().toString(36).slice(2, 10)}`,
          name: formData.name,
          surname: formData.surname,
          phone: formData.phone,
          has_dog: formData.hasDog === 'yes',
          how_heard: formData.howHeard,
          member_name: formData.memberName || null,
          other_explanation: formData.otherExplanation || null
        })
      });

      const data = await response.json();
      console.log('Registration response:', data);

      if (response.ok) {
        // Show success message
        toast.success('✅ Başvurunuz başarıyla alındı! Onay için email kontrol edin.', {
          duration: 5000,
          style: {
            background: '#10b981',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '500'
          }
        });
        
        setTimeout(() => {
          navigate('/portal/login');
        }, 2000);
      } else {
        toast.error(data.detail || 'Kayıt başarısız oldu. Lütfen tekrar deneyin.');
        setMathProblem(generateMathProblem());
        setMathAnswer('');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Sunucu hatası. Lütfen tekrar deneyin.');
      setMathProblem(generateMathProblem());
      setMathAnswer('');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      surname: '',
      email: '',
      phone: '',
      hasDog: 'no',
      howHeard: '',
      memberName: '',
      otherExplanation: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_f40446d8-d7d2-4086-ac19-be3f085557d2/artifacts/2lbvgq55_Cane%20Corso%20Logo-05.png"
            alt="Cane Corso Logo"
            className="h-24 w-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold text-gray-900">{t('portal.register.title')}</h1>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('portal.register.name')} *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={50}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('portal.register.surname')} *
                </label>
                <Input
                  type="text"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  maxLength={50}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('portal.register.email')} *
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('portal.register.phone')} *
              </label>
              <Input
                type="text"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="(5XX) XXX XX XX"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('portal.register.hasDog')} *
              </label>
              <Select value={formData.hasDog} onValueChange={(value) => setFormData({ ...formData, hasDog: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">{t('portal.register.no')}</SelectItem>
                  <SelectItem value="yes">{t('portal.register.yes')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('portal.register.howHeard')} *
              </label>
              <RadioGroup value={formData.howHeard} onValueChange={(value) => setFormData({ ...formData, howHeard: value })}>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="social" id="social" />
                    <Label htmlFor="social">{t('portal.register.socialMedia')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="friend" id="friend" />
                    <Label htmlFor="friend">{t('portal.register.friendRecommendation')}</Label>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="member" id="member" />
                      <Label htmlFor="member">{t('portal.register.throughMember')}</Label>
                    </div>
                    {formData.howHeard === 'member' && (
                      <Input
                        type="text"
                        value={formData.memberName}
                        onChange={(e) => setFormData({ ...formData, memberName: e.target.value })}
                        placeholder={t('portal.register.memberName')}
                        className="ml-6"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="other" id="other" />
                      <Label htmlFor="other">{t('portal.register.other')}</Label>
                    </div>
                    {formData.howHeard === 'other' && (
                      <Input
                        type="text"
                        value={formData.otherExplanation}
                        onChange={(e) => setFormData({ ...formData, otherExplanation: e.target.value })}
                        placeholder={t('portal.register.otherExplanation')}
                        maxLength={100}
                        className="ml-6"
                      />
                    )}
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Math Verification */}
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔒 Güvenlik Doğrulaması
              </label>
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-900">
                  {mathProblem.num1} + {mathProblem.num2} = ?
                </span>
                <Input
                  type="number"
                  value={mathAnswer}
                  onChange={(e) => setMathAnswer(e.target.value)}
                  placeholder="Sonuç"
                  className="w-24"
                  required
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                <UserPlus size={18} className="mr-2" />
                {t('portal.register.registerButton')}
              </Button>
              <Button 
                type="button" 
                onClick={handleCancel}
                variant="outline"
                className="flex-1"
              >
                {t('portal.register.cancel')}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('portal.register.alreadyMember')}{' '}
              <Link to="/portal/login" className="text-red-600 hover:text-red-700 font-medium">
                {t('portal.register.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
